import React from 'react'

import './css/HeroShader.css'

// Raycast-style hero background: a fragment shader painting broad diagonal
// streaks of light in the brand colours over the page background, with an
// ordered (Bayer) dither that turns the gradients into a printed, grainy
// texture instead of smooth banding.
//
// The construction follows the raycast.com hero. Three procedural layers are
// summed: wide bands sliding across the streak direction, a brushed texture
// stretched along it, and thin filaments. The sum is mapped through a
// three-stop colour ramp (low, mid, high); the high stop itself grades from
// the brand blue on the left to the brand purple on the right, like the logo.
// The ramp value is quantised into a few steps with a Bayer threshold, which
// is what gives the pattern its dithered edge.
//
// The pattern settles from a tighter zoom on mount (the cube "scaling in" on
// raycast.com) and follows the pointer with a damped parallax. It only renders
// while near the viewport and the tab is visible, draws a single still frame
// under prefers-reduced-motion, and leaves a CSS gradient in place when WebGL
// is not available.

export interface HeroShaderOptions {
	///@brief   Low stop of the ramp: the page background, so that the quiet
	///         areas dissolve into it and the canvas has no visible edge.
	low: string
	///@brief   Middle stop of the ramp.
	mid: string
	///@brief   High stop of the ramp on the left edge.
	highLeft: string
	///@brief   High stop of the ramp on the right edge.
	highRight: string
	///@brief   Direction of the streaks in degrees, 0 being horizontal and
	///         positive angles rising to the right.
	angle: number
	///@brief   Zoom of the pattern: higher shows more, narrower streaks.
	scale: number
	///@brief   Speed of the drift, 1 being the reference pace.
	speed: number
	///@brief   Dither cell size in CSS pixels.
	dither: number
	///@brief   Number of colour steps across the ramp.
	levels: number
	///@brief   Strength of the pattern, 1 reaching the high stop at its peak.
	intensity: number
	///@brief   Amplitude of the pointer parallax, in pattern units.
	parallax: number
	///@brief   Cap on the device pixel ratio the canvas renders at.
	maxDpr: number
}

///@brief   Light palette for the off-white hero: lavender bands, brand
///         blue-to-purple highlights.
export const LIGHT_PALETTE: Pick<HeroShaderOptions, 'low' | 'mid' | 'highLeft' | 'highRight'> = {
	low: '#FAFAF8',
	mid: '#D4CAEF',
	highLeft: '#7C7CE0',
	highRight: '#C070E8',
}

///@brief   Dark palette, the raycast.com look transposed to the brand. Its
///         low stop is the site's black, the menu bar's, so the quiet areas
///         of the pattern dissolve into the page.
export const DARK_PALETTE: Pick<HeroShaderOptions, 'low' | 'mid' | 'highLeft' | 'highRight'> = {
	low: '#000000',
	mid: '#3A2C6E',
	highLeft: '#7C7CE0',
	highRight: '#C070E8',
}

const DEFAULT_OPTIONS: HeroShaderOptions = {
	...LIGHT_PALETTE,
	angle: 32,
	scale: 1,
	speed: 1,
	dither: 1,
	levels: 7,
	intensity: 1,
	parallax: 0.05,
	maxDpr: 2,
}

type RGB = [ number, number, number ]

function parseColor( s: string, fallback: RGB ): RGB {
	const hex: RegExpExecArray | null = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( s.trim() )
	if ( hex !== null ) {
		return [ parseInt( hex[ 1 ], 16 ) / 255, parseInt( hex[ 2 ], 16 ) / 255, parseInt( hex[ 3 ], 16 ) / 255 ]
	}
	const rgb: RegExpExecArray | null = /^rgba?\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/i.exec( s.trim() )
	if ( rgb !== null ) {
		return [ parseFloat( rgb[ 1 ] ) / 255, parseFloat( rgb[ 2 ] ) / 255, parseFloat( rgb[ 3 ] ) / 255 ]
	}
	return fallback
}

function easeOutCubic( t: number ): number {
	const u: number = 1 - t
	return 1 - u * u * u
}


const VERTEX_SHADER: string = `
attribute vec2 a_position;
void main() {
	gl_Position = vec4( a_position, 0.0, 1.0 );
}
`

const FRAGMENT_SHADER: string = `
precision highp float;

uniform vec2 u_resolution;
uniform float u_time;
uniform vec2 u_mouse;
uniform float u_intro;
uniform vec3 u_low;
uniform vec3 u_mid;
uniform vec3 u_highLeft;
uniform vec3 u_highRight;
uniform float u_angle;
uniform float u_scale;
uniform float u_cell;
uniform float u_levels;
uniform float u_intensity;
uniform float u_parallax;

// 2D simplex noise (Ian McEwan, Ashima Arts).
vec3 mod289( vec3 x ) { return x - floor( x * ( 1.0 / 289.0 ) ) * 289.0; }
vec2 mod289( vec2 x ) { return x - floor( x * ( 1.0 / 289.0 ) ) * 289.0; }
vec3 permute( vec3 x ) { return mod289( ( ( x * 34.0 ) + 1.0 ) * x ); }

float snoise( vec2 v ) {
	const vec4 C = vec4( 0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439 );
	vec2 i = floor( v + dot( v, C.yy ) );
	vec2 x0 = v - i + dot( i, C.xx );
	vec2 i1 = ( x0.x > x0.y ) ? vec2( 1.0, 0.0 ) : vec2( 0.0, 1.0 );
	vec4 x12 = x0.xyxy + C.xxzz;
	x12.xy -= i1;
	i = mod289( i );
	vec3 p = permute( permute( i.y + vec3( 0.0, i1.y, 1.0 ) ) + i.x + vec3( 0.0, i1.x, 1.0 ) );
	vec3 m = max( 0.5 - vec3( dot( x0, x0 ), dot( x12.xy, x12.xy ), dot( x12.zw, x12.zw ) ), 0.0 );
	m = m * m;
	m = m * m;
	vec3 x = 2.0 * fract( p * C.www ) - 1.0;
	vec3 h = abs( x ) - 0.5;
	vec3 ox = floor( x + 0.5 );
	vec3 a0 = x - ox;
	m *= 1.79284291400159 - 0.85373472095314 * ( a0 * a0 + h * h );
	vec3 g;
	g.x = a0.x * x0.x + h.x * x0.y;
	g.yz = a0.yz * x12.xz + h.yz * x12.yw;
	return 130.0 * dot( m, g );
}

float fbm( vec2 p ) {
	float sum = 0.0;
	float amp = 0.5;
	for ( int i = 0; i < 3; i++ ) {
		sum += amp * snoise( p );
		p = p * 2.05 + vec2( 13.7, 7.3 );
		amp *= 0.5;
	}
	return sum;
}

// Ordered dither thresholds: the 2x2 Bayer matrix, refined to 4x4 then 8x8.
float bayer2( vec2 a ) { a = floor( a ); return fract( a.x * 0.5 + a.y * a.y * 0.75 ); }
float bayer4( vec2 a ) { return bayer2( 0.5 * a ) * 0.25 + bayer2( a ); }
float bayer8( vec2 a ) { return bayer4( 0.5 * a ) * 0.25 + bayer2( a ); }

void main() {
	vec2 frag = gl_FragCoord.xy;
	vec2 uv = frag / u_resolution;
	float aspect = u_resolution.x / u_resolution.y;
	vec2 p = ( uv - 0.5 ) * vec2( aspect, 1.0 );

	float t = u_time;

	// Entrance: the pattern settles from a tighter zoom.
	p *= u_scale * mix( 1.4, 1.0, u_intro );
	p += u_mouse * u_parallax * vec2( 1.0, -0.7 );

	// q.x runs along the streaks, q.y across them.
	float ang = u_angle + u_mouse.x * 0.02;
	float c = cos( ang );
	float s = sin( ang );
	vec2 q = vec2( c * p.x - s * p.y, s * p.x + c * p.y );

	// Wide bands sliding across the streak direction.
	float bands = snoise( vec2( q.x * 0.22 + t * 0.03, q.y * 1.7 - t * 0.05 ) );

	// Brushed texture stretched along the streaks, its domain warped so the
	// strokes breathe instead of scrolling rigidly.
	vec2 b = vec2( q.x * 0.6 + t * 0.07, q.y * 6.0 );
	b += 0.3 * vec2( snoise( b * 0.45 + t * 0.04 ), snoise( b * 0.45 - t * 0.03 ) );
	float brush = fbm( b );

	// Thin bright filaments.
	float fil = 1.0 - abs( snoise( vec2( q.x * 0.8 + t * 0.09, q.y * 10.0 + 3.1 ) ) );
	fil = fil * fil;

	float v = bands * 0.8 + brush * 0.5 + ( fil - 0.5 ) * 0.4;
	float nv = clamp( ( v + 1.0 ) * 0.5, 0.0, 1.0 );
	// Contrast curve: the troughs sink back into the page background and
	// only the crests climb to the high stop, leaving clear gaps between
	// the streaks instead of an even wash of the middle tone.
	nv = smoothstep( 0.33, 0.92, nv );

	// The streaks are strongest around the centre, behind the title and the
	// body copy, and fade toward the edges.
	float radial = 1.0 - smoothstep( 0.1, 1.05, length( ( p - vec2( 0.0, 0.02 ) ) * vec2( 0.65, 1.25 ) ) );
	nv *= u_intensity * ( 0.3 + 0.7 * radial );

	// Quantise the ramp value with an ordered threshold: the dither lives in
	// the transitions between colour steps, and a value of 0 stays exactly
	// the low stop so the background remains flat.
	float steps = max( u_levels - 1.0, 1.0 );
	float threshold = bayer8( floor( frag / u_cell ) ) - 0.5;
	nv = clamp( floor( nv * steps + threshold + 0.5 ) / steps, 0.0, 1.0 );

	vec3 high = mix( u_highLeft, u_highRight, smoothstep( 0.0, 1.0, uv.x ) );
	vec3 col = nv < 0.5 ? mix( u_low, u_mid, nv * 2.0 ) : mix( u_mid, high, ( nv - 0.5 ) * 2.0 );

	gl_FragColor = vec4( col, 1.0 );
}
`

const UNIFORM_NAMES: string[] = [
	'u_resolution', 'u_time', 'u_mouse', 'u_intro',
	'u_low', 'u_mid', 'u_highLeft', 'u_highRight',
	'u_angle', 'u_scale', 'u_cell', 'u_levels', 'u_intensity', 'u_parallax',
]

///@brief   Duration of the entrance zoom, in seconds.
const INTRO_DURATION: number = 1.4
///@brief   Time shown by the still frame under reduced motion.
const STILL_TIME: number = 12


function compileShader( gl: WebGLRenderingContext, type: number, source: string ): WebGLShader | null {
	const shader: WebGLShader | null = gl.createShader( type )
	if ( shader === null ) return null
	gl.shaderSource( shader, source )
	gl.compileShader( shader )
	if ( !gl.getShaderParameter( shader, gl.COMPILE_STATUS ) ) {
		console.warn( 'HeroShader: shader compilation failed', gl.getShaderInfoLog( shader ) )
		gl.deleteShader( shader )
		return null
	}
	return shader
}


///@brief   One shader canvas filling its host element.
class ShaderField {

	private element: HTMLElement
	private canvas: HTMLCanvasElement
	private gl: WebGLRenderingContext | null = null
	private program: WebGLProgram | null = null
	private buffer: WebGLBuffer | null = null
	private uniforms: { [ name: string ]: WebGLUniformLocation | null } = {}

	private options: HeroShaderOptions
	private low: RGB
	private mid: RGB
	private highLeft: RGB
	private highRight: RGB

	private width: number = 0
	private height: number = 0
	private dpr: number = 1

	private bRunning: boolean = false
	private bVisible: boolean = true
	private bReducedMotion: boolean = false
	private bContextLost: boolean = false
	private bReady: boolean = false
	private rafId: number | null = null

	///@brief   Origin of the shader clock and of the entrance, in ms.
	private startTime: number = 0
	private lastFrameTime: number = 0
	///@brief   Time offset so that each visit starts at a different frame.
	private seed: number = Math.random() * 1000

	///@brief   Pointer position in [-1, 1] over the host; the drawn value
	///         chases the target with a damped lag.
	private mouseTarget: { x: number, y: number } = { x: 0, y: 0 }
	private mouse: { x: number, y: number } = { x: 0, y: 0 }

	private resizeObserver: ResizeObserver | null = null
	private intersectionObserver: IntersectionObserver | null = null
	private reducedMotionQuery: MediaQueryList | null = null

	constructor( element: HTMLElement, options: HeroShaderOptions ) {
		this.element = element
		this.options = options

		this.low = parseColor( options.low, [ 0.98, 0.98, 0.973 ] )
		this.mid = parseColor( options.mid, [ 0.863, 0.839, 0.933 ] )
		this.highLeft = parseColor( options.highLeft, [ 0.486, 0.486, 0.878 ] )
		this.highRight = parseColor( options.highRight, [ 0.753, 0.439, 0.91 ] )

		while ( element.firstChild ) element.removeChild( element.firstChild )
		element.classList.remove( 'is-fallback' )

		this.canvas = document.createElement( 'canvas' )
		element.appendChild( this.canvas )

		this.canvas.addEventListener( 'webglcontextlost', this )
		this.canvas.addEventListener( 'webglcontextrestored', this )

		if ( !this.initGl() ) {
			element.classList.add( 'is-fallback' )
			element.removeChild( this.canvas )
			return
		}

		if ( typeof window.matchMedia === 'function' ) {
			this.reducedMotionQuery = window.matchMedia( '(prefers-reduced-motion: reduce)' )
			this.bReducedMotion = this.reducedMotionQuery.matches
			this.reducedMotionQuery.addEventListener( 'change', this )
		}

		if ( typeof ResizeObserver !== 'undefined' ) {
			this.resizeObserver = new ResizeObserver( (): void => { this.onResize() } )
			this.resizeObserver.observe( element )
		} else {
			window.addEventListener( 'resize', this )
		}

		// Only render while the hero is near the viewport; the margin keeps
		// it from flipping on and off with every small scroll at the edge.
		if ( typeof IntersectionObserver !== 'undefined' ) {
			this.intersectionObserver = new IntersectionObserver( ( entries: IntersectionObserverEntry[] ): void => {
				entries.forEach( ( entry: IntersectionObserverEntry ): void => {
					this.bVisible = entry.isIntersecting
				} )
				this.syncLoop()
			}, { rootMargin: '30% 0px 30% 0px' } )
			this.intersectionObserver.observe( element )
		}

		document.addEventListener( 'visibilitychange', this )
		// The canvas lets pointer events through, so the pointer is read at
		// the window level and mapped onto the host.
		window.addEventListener( 'mousemove', this )

		this.startTime = performance.now()
		this.updateSize()
	}

	///@brief   Creates the context, program and geometry. False when WebGL
	///         is unavailable, in which case the host keeps its CSS fallback.
	private initGl(): boolean {
		let gl: WebGLRenderingContext | null = null
		try {
			gl = this.canvas.getContext( 'webgl', { alpha: false, antialias: false, depth: false, stencil: false, preserveDrawingBuffer: false } )
				|| ( this.canvas.getContext( 'experimental-webgl', { alpha: false, antialias: false, depth: false, stencil: false } ) as WebGLRenderingContext | null )
		} catch ( e ) {
			gl = null
		}
		if ( gl === null ) return false

		const vertex: WebGLShader | null = compileShader( gl, gl.VERTEX_SHADER, VERTEX_SHADER )
		const fragment: WebGLShader | null = compileShader( gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER )
		if ( vertex === null || fragment === null ) return false

		const program: WebGLProgram | null = gl.createProgram()
		if ( program === null ) return false
		gl.attachShader( program, vertex )
		gl.attachShader( program, fragment )
		gl.linkProgram( program )
		gl.deleteShader( vertex )
		gl.deleteShader( fragment )
		if ( !gl.getProgramParameter( program, gl.LINK_STATUS ) ) {
			console.warn( 'HeroShader: program link failed', gl.getProgramInfoLog( program ) )
			gl.deleteProgram( program )
			return false
		}
		gl.useProgram( program )

		// One triangle covering the whole clip space.
		const buffer: WebGLBuffer | null = gl.createBuffer()
		if ( buffer === null ) return false
		gl.bindBuffer( gl.ARRAY_BUFFER, buffer )
		gl.bufferData( gl.ARRAY_BUFFER, new Float32Array( [ -1, -1, 3, -1, -1, 3 ] ), gl.STATIC_DRAW )
		const position: number = gl.getAttribLocation( program, 'a_position' )
		gl.enableVertexAttribArray( position )
		gl.vertexAttribPointer( position, 2, gl.FLOAT, false, 0, 0 )

		this.uniforms = {}
		for ( let i: number = 0; i < UNIFORM_NAMES.length; i++ ) {
			this.uniforms[ UNIFORM_NAMES[ i ] ] = gl.getUniformLocation( program, UNIFORM_NAMES[ i ] )
		}

		gl.uniform3fv( this.uniforms[ 'u_low' ], this.low )
		gl.uniform3fv( this.uniforms[ 'u_mid' ], this.mid )
		gl.uniform3fv( this.uniforms[ 'u_highLeft' ], this.highLeft )
		gl.uniform3fv( this.uniforms[ 'u_highRight' ], this.highRight )
		gl.uniform1f( this.uniforms[ 'u_angle' ], this.options.angle * Math.PI / 180 )
		gl.uniform1f( this.uniforms[ 'u_scale' ], this.options.scale )
		gl.uniform1f( this.uniforms[ 'u_levels' ], this.options.levels )
		gl.uniform1f( this.uniforms[ 'u_intensity' ], this.options.intensity )
		gl.uniform1f( this.uniforms[ 'u_parallax' ], this.options.parallax )

		this.gl = gl
		this.program = program
		this.buffer = buffer
		this.bContextLost = false
		return true
	}

	handleEvent( e: Event ): void {
		switch ( e.type ) {
			case 'resize':
				this.onResize()
				break
			case 'visibilitychange':
				this.syncLoop()
				break
			case 'mousemove':
				this.onMouseMove( e as MouseEvent )
				break
			case 'change':
				this.bReducedMotion = this.reducedMotionQuery !== null && this.reducedMotionQuery.matches
				this.syncLoop()
				this.drawFrame( performance.now() )
				break
			case 'webglcontextlost':
				e.preventDefault()
				this.bContextLost = true
				this.stop()
				break
			case 'webglcontextrestored':
				if ( this.initGl() ) {
					this.updateSize()
					this.syncLoop()
				}
				break
		}
	}

	private onResize(): void {
		this.updateSize()
		this.drawFrame( performance.now() )
	}

	private onMouseMove( e: MouseEvent ): void {
		if ( this.bReducedMotion ) return
		const rect: DOMRect = this.element.getBoundingClientRect()
		if ( rect.width <= 0 || rect.height <= 0 ) return
		const x: number = ( ( e.clientX - rect.left ) / rect.width ) * 2 - 1
		const y: number = ( ( e.clientY - rect.top ) / rect.height ) * 2 - 1
		this.mouseTarget.x = Math.max( -1.5, Math.min( 1.5, x ) )
		this.mouseTarget.y = Math.max( -1.5, Math.min( 1.5, y ) )
	}

	private updateSize(): void {
		this.width = this.element.clientWidth
		this.height = this.element.clientHeight
		this.dpr = Math.min( window.devicePixelRatio || 1, this.options.maxDpr )

		this.canvas.width = Math.max( 1, Math.round( this.width * this.dpr ) )
		this.canvas.height = Math.max( 1, Math.round( this.height * this.dpr ) )
		this.canvas.style.width = this.width + 'px'
		this.canvas.style.height = this.height + 'px'

		if ( this.gl !== null && !this.bContextLost ) {
			this.gl.viewport( 0, 0, this.canvas.width, this.canvas.height )
			this.gl.uniform2f( this.uniforms[ 'u_resolution' ], this.canvas.width, this.canvas.height )
			// Dither cells are sized in CSS pixels, so the grain reads the
			// same on every screen density.
			this.gl.uniform1f( this.uniforms[ 'u_cell' ], Math.max( 1, Math.round( this.options.dither * this.dpr ) ) )
		}
	}

	private drawFrame( now: number ): void {
		const gl: WebGLRenderingContext | null = this.gl
		if ( gl === null || this.bContextLost ) return

		let time: number
		let intro: number
		if ( this.bReducedMotion ) {
			time = STILL_TIME + this.seed
			intro = 1
			this.mouse.x = 0
			this.mouse.y = 0
		} else {
			const elapsed: number = ( now - this.startTime ) * 0.001
			time = elapsed * this.options.speed + this.seed
			intro = easeOutCubic( Math.min( 1, elapsed / INTRO_DURATION ) )
		}

		gl.uniform1f( this.uniforms[ 'u_time' ], time )
		gl.uniform1f( this.uniforms[ 'u_intro' ], intro )
		gl.uniform2f( this.uniforms[ 'u_mouse' ], this.mouse.x, this.mouse.y )
		gl.drawArrays( gl.TRIANGLES, 0, 3 )

		if ( !this.bReady ) {
			this.bReady = true
			this.canvas.classList.add( 'is-ready' )
		}
	}

	private animate( timestamp: number ): void {
		if ( !this.bRunning ) {
			this.rafId = null
			return
		}

		// Clamp the frame time so a tab coming back from the background does
		// not jump the parallax in one step.
		const dt: number = Math.min( ( timestamp - this.lastFrameTime ) * 0.001, 0.05 )
		this.lastFrameTime = timestamp

		// Damped chase of the pointer.
		const k: number = 1 - Math.exp( -dt * 3.5 )
		this.mouse.x += ( this.mouseTarget.x - this.mouse.x ) * k
		this.mouse.y += ( this.mouseTarget.y - this.mouse.y ) * k

		this.drawFrame( timestamp )

		this.rafId = window.requestAnimationFrame( ( ts: number ): void => { this.animate( ts ) } )
	}

	private syncLoop(): void {
		const bShouldRun: boolean = this.bVisible && !document.hidden && !this.bReducedMotion && !this.bContextLost
		if ( bShouldRun ) {
			this.start()
		} else {
			this.stop()
		}
	}

	public start(): void {
		if ( this.bRunning || this.gl === null ) return
		if ( !this.bVisible || document.hidden || this.bReducedMotion || this.bContextLost ) {
			this.drawFrame( performance.now() )
			return
		}
		this.bRunning = true
		this.lastFrameTime = performance.now()
		this.rafId = window.requestAnimationFrame( ( ts: number ): void => { this.animate( ts ) } )
	}

	public stop(): void {
		this.bRunning = false
		if ( this.rafId !== null ) {
			window.cancelAnimationFrame( this.rafId )
			this.rafId = null
		}
	}

	public destroy(): void {
		this.stop()
		if ( this.resizeObserver !== null ) this.resizeObserver.disconnect()
		if ( this.intersectionObserver !== null ) this.intersectionObserver.disconnect()
		if ( this.reducedMotionQuery !== null ) this.reducedMotionQuery.removeEventListener( 'change', this )
		window.removeEventListener( 'resize', this )
		window.removeEventListener( 'mousemove', this )
		document.removeEventListener( 'visibilitychange', this )
		this.canvas.removeEventListener( 'webglcontextlost', this )
		this.canvas.removeEventListener( 'webglcontextrestored', this )

		if ( this.gl !== null ) {
			if ( this.buffer !== null ) this.gl.deleteBuffer( this.buffer )
			if ( this.program !== null ) this.gl.deleteProgram( this.program )
			const lose = this.gl.getExtension( 'WEBGL_lose_context' )
			if ( lose !== null ) lose.loseContext()
			this.gl = null
		}
		if ( this.canvas.parentNode === this.element ) this.element.removeChild( this.canvas )
	}

}


export interface HeroShaderProps extends Partial<HeroShaderOptions> {
	className?: string
}

///@brief   Decorative shader canvas filling its positioned parent. Purely
///         visual: hidden from assistive tech and transparent to the mouse.
export function HeroShader( props: HeroShaderProps ): React.JSX.Element {

	const {
		className,
		low = DEFAULT_OPTIONS.low,
		mid = DEFAULT_OPTIONS.mid,
		highLeft = DEFAULT_OPTIONS.highLeft,
		highRight = DEFAULT_OPTIONS.highRight,
		angle = DEFAULT_OPTIONS.angle,
		scale = DEFAULT_OPTIONS.scale,
		speed = DEFAULT_OPTIONS.speed,
		dither = DEFAULT_OPTIONS.dither,
		levels = DEFAULT_OPTIONS.levels,
		intensity = DEFAULT_OPTIONS.intensity,
		parallax = DEFAULT_OPTIONS.parallax,
		maxDpr = DEFAULT_OPTIONS.maxDpr,
	} = props

	const ref = React.useRef<HTMLDivElement>( null )

	React.useEffect( (): ( () => void ) | undefined => {
		const element: HTMLDivElement | null = ref.current
		if ( element === null ) return undefined

		const field: ShaderField = new ShaderField( element, { low, mid, highLeft, highRight, angle, scale, speed, dither, levels, intensity, parallax, maxDpr } )
		field.start()

		return (): void => { field.destroy() }
	}, [ low, mid, highLeft, highRight, angle, scale, speed, dither, levels, intensity, parallax, maxDpr ] )

	return (
		<div ref={ ref } className={ `HeroShader${ className ? ' ' + className : '' }` } aria-hidden="true" />
	)
}
